import { Trans } from "react-i18next";
import { t } from "i18next";
import type { ChangeEvent } from "react";
import { Input } from "antd";
import { useSetState } from "ahooks";
import dayjs from "dayjs";
import type { LinkedAccount } from "@store/linkedAccountSlice";
import { ProductPicker } from "./ProductPicker";
import { twMerge } from "tailwind-merge";
import { ClipSwitch } from "@components/ClipSwitch";
import { UserPublishSubmitRequestPublishMode } from "@apis/generated/openapi";
import { UserPublishSubmitRequest } from "@apis/http/api";
import { ScheduledDate } from "./ScheduledDate";
import { normalizeNameWithAt } from "@utils/util";

export interface PostToTikTokFormState extends Pick<
  UserPublishSubmitRequest,
  "needPrecheck" | "publishTime" | "publishMode"
> {
  /** video description */
  description: string;
  /** Short Product Title */
  title: string;
  productId: string;
  productTitle: string;
  productImage: string;
  salesCount?: number;
  /** 发布记录 id */
  postRecordId: number | undefined;
}

export interface FormBodyProps {
  className?: string;
  account: LinkedAccount;
  formState: PostToTikTokFormState;
  setFormState: ReturnType<typeof useSetState<PostToTikTokFormState>>[1];
  disabled?: boolean;
  scheduledDateError?: boolean;
  titleError?: boolean;
  titleBannedCharsDisplay?: string;
  onTitleBlur?: () => void;
}

export const FormBody = (props: FormBodyProps) => {
  const {
    account,
    formState,
    setFormState,
    disabled,
    className,
    scheduledDateError,
    titleError,
    titleBannedCharsDisplay,
    onTitleBlur,
  } = props;
  const { needPrecheck } = formState;
  const accountName = account.accountName ?? "";

  const labelClassName = "flex h-[30px] items-center text-[16px] font-bold leading-[22px] text-kds-fg";

  return (
    <div className={twMerge("flex flex-col", className)}>
      <div className="flex py-[12px] px-[16px] items-center bg-kds-bg-overlay text-[14px] leading-[20px] text-kds-fg-soft">
        <Trans
          i18nKey="Posting as {{account_name}}"
          defaults="Posting as <1>{{account_name}}</1>"
          values={{ account_name: normalizeNameWithAt(accountName) }}
          components={{ 1: <span className="ml-[4px] text-kds-fg font-bold text-[16px] leading-[22px]" /> }}
        />
      </div>

      <div className="flex flex-col gap-[16px] px-[24px] pt-[16px] pb-[40px]">
        <div className="flex flex-col gap-[4px]">
          <label className={labelClassName}>
            <Trans>Video Description</Trans>
            <span className="ml-[2px] text-kds-danger">*</span>
          </label>
          <Input.TextArea
            showCount
            autoSize={{ minRows: 5, maxRows: 10 }}
            placeholder={t("e.g., Sharing a product I've been loving lately")}
            maxLength={4000}
            value={formState.description}
            disabled={disabled}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFormState({ description: e.target.value })}
            styles={{
              count: {
                bottom: 8,
                right: 12,
                fontSize: 12,
                color: "var(--ant-color-text-placeholder)",
              },
            }}
          />
          <div className="text-[12px] leading-[16px] text-kds-fg-subtle">{t("Type # to add hashtags")}</div>
        </div>

        <div className="h-px w-full bg-kds-border" aria-hidden />

        <div className="flex flex-col gap-[16px]">
          <div className="flex flex-col gap-[4px]">
            <label className={labelClassName}>
              <Trans>Product to Promote</Trans>
              <span className="ml-[2px] text-kds-danger">*</span>
            </label>
            <ProductPicker
              linkId={account.id!}
              value={formState}
              onChange={(next) => {
                const { productId, productTitle, productImage, salesCount } = next;
                setFormState({
                  productId,
                  productTitle,
                  productImage,
                  salesCount,
                });
              }}
              disabled={disabled}
            />
          </div>

          <div className="flex flex-col gap-[4px]">
            <label className={labelClassName}>
              <Trans>Short Product Title</Trans>
            </label>
            <Input
              placeholder={t("e.g., 100% Cotton Short-Sleeve T-shirt")}
              maxLength={30}
              value={formState.title}
              disabled={disabled}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setFormState({ title: e.target.value })}
              onBlur={onTitleBlur}
            />
            {titleError ? (
              <div className="text-kds-danger text-[12px] leading-[16px] font-normal">
                {t("Cannot contain: {{ban_characters}}", {
                  ban_characters: titleBannedCharsDisplay ?? "",
                })}
              </div>
            ) : null}
          </div>
        </div>

        <div className="h-px w-full bg-kds-border" aria-hidden />

        <div className="flex flex-col gap-[16px]">
          <ClipSwitch
            checked={needPrecheck}
            onChange={(nextValue) => setFormState({ needPrecheck: nextValue })}
            title={t("Video Pre-check")}
            description={t("Use TikTok’s official video compliance check to prevent account issues")}
          />

          <ScheduledDate
            disabled={disabled}
            error={scheduledDateError}
            errorText="Please select a publish time."
            value={{
              scheduled: formState.publishMode === UserPublishSubmitRequestPublishMode.SCHEDULE,
              time: formState.publishTime ? dayjs(formState.publishTime) : undefined,
            }}
            onChange={(nextValue) => {
              if (!nextValue.scheduled) {
                setFormState({
                  publishMode: UserPublishSubmitRequestPublishMode.NOW,
                  publishTime: undefined,
                });
                return;
              }
              setFormState({
                publishMode: UserPublishSubmitRequestPublishMode.SCHEDULE,
                publishTime: nextValue.time?.valueOf(),
              });
            }}
          />
        </div>
      </div>
    </div>
  );
};
